import { Space, Card, Select, Input, Button, Typography, AutoComplete } from 'antd';
import Title from 'antd/es/typography/Title';
import { v4 as uuidv4 } from 'uuid';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import React from 'react';
import TipTap from '../../Sections/TipTap';

interface CorsiaProps {
  data: any;
  setData: (path: string, value: any) => void;
  readonly?: boolean;
}

const Corsia: React.FC<CorsiaProps> = ({ data, setData, readonly = false }) => {
  const uniqueStandards = Array.from(
    new Set(data.data.map((methodology) => methodology.standard).filter(Boolean))
  ) as string[];

  const allVersions = Array.from(
    new Set(data.data.map((methodology) => methodology.version).filter(Boolean))
  ) as string[];

  const getVersionsForStandard = (standard: string): string[] => {
    const versions: string[] = Array.from(
      new Set(
        data.data
          .filter((methodology) => methodology.standard === standard)
          .map((methodology) => methodology.version)
          .filter(Boolean)
      )
    );
    return versions.length ? versions : allVersions;
  };

  const handleAddCorsia = () => {
    if (readonly) return;
    const newCorsiaData = [
      ...data.corsia.data,
      {
        id: uuidv4(),
        standard: '',
        methodology: '',
        version: [],
        eligibility: null
      }
    ];
    setData('methodologies.corsia.data', newCorsiaData);
  };

  const handleRemoveCorsia = (id: string) => {
    if (readonly) return;
    const updatedCorsiaData = data.corsia.data.filter((item) => item.id !== id);
    setData('methodologies.corsia.data', updatedCorsiaData);
  };

  const updateCorsiaField = (id: string, field: string, value: any) => {
    if (readonly) return;
    const updatedCorsiaData = data.corsia.data.map((item) => {
      if (item.id === id) {
        return { ...item, [field]: value };
      }
      return item;
    });
    setData('methodologies.corsia.data', updatedCorsiaData);
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Title level={5}>Corsia</Title>
      {data.corsia.data.map((corsiaItem, index) => (
        <Card key={corsiaItem.id} style={{ marginBottom: '16px' }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography.Title level={5}>Corsia Entry #{index + 1}</Typography.Title>
              <Button
                type="primary"
                danger
                disabled={readonly}
                onClick={() => handleRemoveCorsia(corsiaItem.id)}>
                Remove
              </Button>
            </div>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Typography.Text>Standard</Typography.Text>
              <AutoComplete
                style={{ width: '100%' }}
                value={corsiaItem.standard}
                disabled={readonly}
                onChange={(value) => updateCorsiaField(corsiaItem.id, 'standard', value)}
                placeholder="Select Standard">
                {uniqueStandards.map((standard) => (
                  <Select.Option key={standard} value={standard}>
                    {standard}
                  </Select.Option>
                ))}
              </AutoComplete>
            </Space>

            <Space direction="vertical" style={{ width: '100%', marginTop: '10px' }}>
              <Typography.Text>Version</Typography.Text>
              <Select
                style={{ width: '100%' }}
                mode="multiple"
                value={corsiaItem.version}
                onChange={(value) => updateCorsiaField(corsiaItem.id, 'version', value)}
                placeholder="Select Version"
                disabled={readonly || !corsiaItem.standard}>
                {corsiaItem.standard &&
                  getVersionsForStandard(corsiaItem.standard).map((version) => (
                    <Select.Option key={version} value={version}>
                      {version}
                    </Select.Option>
                  ))}
              </Select>
            </Space>

            <Space direction="vertical" style={{ width: '100%', marginTop: '10px' }}>
              <Typography.Text>Methodology</Typography.Text>
              <Input
                value={corsiaItem.methodology}
                disabled={readonly}
                onChange={(e) => updateCorsiaField(corsiaItem.id, 'methodology', e.target.value)}
                placeholder="Enter Methodology"
              />
            </Space>

            <Space direction="vertical" style={{ width: '100%', marginTop: '10px' }}>
              <Typography.Text>Eligibility</Typography.Text>
              <TipTap
                value={corsiaItem.eligibility}
                readonly={readonly}
                onChange={(value) => updateCorsiaField(corsiaItem.id, 'eligibility', value)}
              />
            </Space>
          </Space>
        </Card>
      ))}
      <Button type="primary" disabled={readonly} onClick={handleAddCorsia}>
        Add Corsia Entry
      </Button>
    </Space>
  );
};

export default Corsia;
